import React, { useEffect, useMemo, useRef } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { PeekBox, PeekButton, useTheme } from "@piiqtechnologies/ui-components";
import Edit2 from "@piiqtechnologies/svg-icons/lib/Edit2";
import { useError, useGetAssessmentTemplateTrackedCompetitors, useGetTrackedCompetitors, } from "@piiqtechnologies/ui-lib";
import { PATHS } from "@constants/Paths";
import { CategoryCard } from "../category-card/CategoryCard";
import { RankingWidgetShell, RankingChart, RankingBalloonMarker, RankingRow, logPosition } from "../ranking-widget";
import rankingWidgetMessages from "../ranking-widget/messages.i18n";
import { TrackedCompetitorsMarkers } from "./TrackedCompetitorsMarkers";
import { TrackedCompetitorsMessageState } from "./TrackedCompetitorsMessageState";
import { assignCompetitorColor } from "./colors";
import { TRACKED_COMPETITORS_SECTION, isAbsoluteZero } from "./constants";
import { WidgetLoader } from "../widget-loader";
import messages from "./messages.i18n";
export const TrackedCompetitorsWidget = ({ subjectId, assessmentId, reportId, assessmentTemplateId, }) => {
    var _a, _b;
    const intl = useIntl();
    const navigate = useNavigate();
    const theme = useTheme();
    const { setError } = useError();
    const [fetchTrackedCompetitors, data, loading, error] = useGetTrackedCompetitors();
    const [fetchTemplateCompetitors, templateCompetitors, templateLoading, templateError] = useGetAssessmentTemplateTrackedCompetitors();
    useEffect(() => {
        if (subjectId) {
            fetchTrackedCompetitors(subjectId, assessmentId);
        }
    }, [subjectId, assessmentId, fetchTrackedCompetitors]);
    useEffect(() => {
        if (subjectId) {
            fetchTemplateCompetitors(subjectId);
        }
    }, [subjectId, fetchTemplateCompetitors]);
    useEffect(() => {
        if (error)
            setError(error.message);
    }, [error, setError]);
    useEffect(() => {
        if (templateError)
            setError(templateError.message);
    }, [templateError, setError]);
    const competitors = (_a = data === null || data === void 0 ? void 0 : data.trackedCompetitors) !== null && _a !== void 0 ? _a : [];
    const isPopulated = competitors.length > 0;
    const scoredFetchStarted = useRef(false);
    useEffect(() => {
        if (loading)
            scoredFetchStarted.current = true;
    }, [loading]);
    const scoredSettled = scoredFetchStarted.current && !loading;
    const isPending = scoredSettled && !error && data === null;
    const pendingVariant = ((_b = templateCompetitors === null || templateCompetitors === void 0 ? void 0 : templateCompetitors.length) !== null && _b !== void 0 ? _b : 0) > 0 ? "configured" : "empty";
    const benchmark = data === null || data === void 0 ? void 0 : data.appearanceRateBenchmark;
    const distribution = benchmark === null || benchmark === void 0 ? void 0 : benchmark.distribution;
    const targetSubject = useMemo(() => data && benchmark
        ? {
            subjectName: data.subjectName,
            appearanceRate: benchmark.yourValue,
            rank: benchmark.yourRank,
            totalCompetitors: benchmark.totalCompetitors,
        }
        : undefined, [data, benchmark]);
    const goToSettings = () => {
        var _a;
        const targetId = (_a = assessmentTemplateId !== null && assessmentTemplateId !== void 0 ? assessmentTemplateId : reportId) !== null && _a !== void 0 ? _a : subjectId;
        if (!targetId)
            return;
        navigate(`${PATHS.REPORT_SETTINGS.replace(":reportId", targetId)}?section=${TRACKED_COMPETITORS_SECTION}`, {
            state: { subjectId },
        });
    };
    const targetPct = distribution && targetSubject ? logPosition(targetSubject.appearanceRate, distribution.min, distribution.max) : 0;
    const chart = !isPending && distribution && targetSubject ? (React.createElement(RankingChart, { min: distribution.min, max: distribution.max, minTooltip: String(Math.round(distribution.min)), maxTooltip: String(Math.round(distribution.max)), balloon: React.createElement(RankingBalloonMarker, { pct: targetPct, label: intl.formatMessage(messages.targetCallout, {
                rank: targetSubject.rank,
            }), tooltipTitle: intl.formatMessage(rankingWidgetMessages.tooltipYou, {
                rank: targetSubject.rank,
                total: targetSubject.totalCompetitors,
            }) }), markers: React.createElement(TrackedCompetitorsMarkers, { min: distribution.min, max: distribution.max, targetSubject: targetSubject, competitors: competitors }) })) : undefined;
    const isLoading = (!!subjectId && !scoredSettled) || (isPending && templateLoading);
    const editCompSetAction = isPopulated && !isLoading ? (React.createElement(PeekButton, { variant: "outlined", color: "info", size: "small", startIcon: React.createElement(Edit2, { width: 16, height: 16 }), onClick: goToSettings }, intl.formatMessage(messages.editCompSet))) : undefined;
    return (React.createElement(CategoryCard, { categoryName: intl.formatMessage(messages.title), titleInfoTooltip: intl.formatMessage(messages.infoTooltip), action: editCompSetAction }, isLoading ? (React.createElement(WidgetLoader, null)) : (React.createElement(RankingWidgetShell, { chart: chart, isEmpty: isPending || !isPopulated }, isPending ? (React.createElement(TrackedCompetitorsMessageState, { heading: intl.formatMessage(messages.pendingHeading), subtext: intl.formatMessage(pendingVariant === "configured" ? messages.pendingSubtextConfigured : messages.pendingSubtextEmpty), buttonLabel: intl.formatMessage(pendingVariant === "configured" ? messages.editCompSet : messages.addCompetitors), onButtonClick: goToSettings })) : isPopulated ? (competitors.map((c, i) => (React.createElement(RankingRow, { key: c.subjectId, rank: isAbsoluteZero(c) ? null : c.rank, name: c.subjectName, appearanceRate: c.appearanceRate, bestPerforming: isAbsoluteZero(c) ? [] : c.bestPerformingSubcategories, mostCited: isAbsoluteZero(c) ? [] : c.mostCitedBy, leading: isAbsoluteZero(c) ? undefined : (React.createElement(PeekBox, { "data-testid": `competitor-bullet-${c.subjectId}`, sx: {
                width: 12,
                height: 12,
                borderRadius: "50%",
                bgcolor: assignCompetitorColor(i, theme.palette.graphColors),
                flexShrink: 0,
            } })), bestPerformingLabel: intl.formatMessage(messages.bestPerforming, { features: "{features}" }), mostCitedLabel: intl.formatMessage(messages.mostCitedBy), rankNotAvailableLabel: intl.formatMessage(messages.rankNotAvailable), scoreNotAvailableLabel: intl.formatMessage(messages.scoreNotAvailable), invisibleLabel: isAbsoluteZero(c) ? intl.formatMessage(messages.invisibleLabel) : undefined, emptyValueLabel: intl.formatMessage(messages.noneLabel) })))) : (React.createElement(TrackedCompetitorsMessageState, { heading: intl.formatMessage(messages.emptyHeading), subtext: intl.formatMessage(messages.emptySubtext), buttonLabel: intl.formatMessage(messages.addCompetitors), onButtonClick: goToSettings }))))));
};
export default TrackedCompetitorsWidget;
